import { useEffect, useMemo, useRef, useState } from "react";
import { Trash2, Play, Briefcase, Tag as TagIcon, Layers, Search, Plus, Check, AlertTriangle } from "lucide-react";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { DatePicker } from "@/components/ui/date-picker";
import { TimePicker } from "@/components/ui/time-picker";
import EntryEditor from "@/components/week-calendar/EntryEditor";

// ---------- formatting helpers ----------
function pad2(n) { return String(n).padStart(2, "0"); }
function fmtDuration(seconds) {
  if (!seconds || seconds < 0) return "0:00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h}:${pad2(m)}:${s.toString().padStart(2, "0")}`;
}
function fmtHM(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${pad2(m)}m`;
}
function toDateStr(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function toTimeStr(d) {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function parseDateTime(dateStr, timeStr) {
  const [y, m, day] = dateStr.split("-").map(Number);
  const [h, min] = timeStr.split(":").map(Number);
  return new Date(y, (m || 1) - 1, day || 1, h || 0, min || 0, 0, 0);
}
function dayHeading(d) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const diff = Math.round((today - day) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}
function arraysEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

// ---------- inline pickers ----------
function ClientPickerInline({ items, value, onChange, entryId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  useEffect(() => { if (open) setSearch(""); }, [open]);
  const selected = (value || []).map((id) => items.find((c) => c.id === id)).filter(Boolean);
  const query = search.trim().toLowerCase();
  const filtered = items.filter((c) => !query || c.name.toLowerCase().includes(query));
  const toggle = (id) => {
    const next = (value || []).includes(id)
      ? (value || []).filter((x) => x !== id)
      : [...(value || []), id];
    onChange(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-7 px-1.5 rounded hover:bg-slate-100 text-slate-600 flex items-center gap-1 text-xs max-w-[180px]"
          data-testid={`list-client-picker-${entryId}`}
        >
          {selected.length === 0 ? (
            <>
              <Briefcase className="h-3 w-3 text-slate-400 shrink-0" />
              <span className="text-slate-400">Client</span>
            </>
          ) : (
            <span className="flex items-center gap-1 overflow-hidden">
              {selected.slice(0, 2).map((c) => (
                <span key={c.id}
                      className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
                      style={{ background: `${c.color}1a`, color: c.color }}>
                  {c.name}
                </span>
              ))}
              {selected.length > 2 && (
                <span className="text-[10px] text-slate-500">+{selected.length - 2}</span>
              )}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="flex items-center gap-1 mb-2">
          <Search className="h-3.5 w-3.5 text-slate-400" />
          <input value={search}
                 onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search clients…"
                 className="flex-1 text-sm px-1 py-1 outline-none placeholder:text-slate-400"
                 data-testid={`list-client-search-${entryId}`} />
        </div>
        <div className="max-h-48 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-2 py-3 text-xs text-slate-400 text-center">No clients.</div>
          ) : filtered.map((c) => {
            const isOn = (value || []).includes(c.id);
            return (
              <button
                key={c.id}
                onClick={() => toggle(c.id)}
                className={`w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 text-sm ${isOn ? "bg-slate-50" : ""}`}
                data-testid={`list-client-option-${entryId}-${c.id}`}
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />
                <span className="flex-1 truncate">{c.name}</span>
                {isOn && <Check className="h-3.5 w-3.5 text-[#145c8c]" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SinglePickerInline({ icon: Icon, items, value, onChange, placeholder, entryId, testIdSuffix, pill }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  useEffect(() => { if (open) setSearch(""); }, [open]);
  const selected = items.find((i) => i.id === value) || null;
  const query = search.trim().toLowerCase();
  const filtered = items.filter((i) => !query || i.name.toLowerCase().includes(query));

  const pick = (id) => {
    onChange(id === value ? null : id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-7 px-1.5 rounded hover:bg-slate-100 text-slate-600 flex items-center gap-1 text-xs max-w-[180px]"
          data-testid={`list-${testIdSuffix}-picker-${entryId}`}
        >
          {!selected ? (
            <>
              <Icon className="h-3 w-3 text-slate-400 shrink-0" />
              <span className="text-slate-400">{placeholder}</span>
            </>
          ) : pill ? (
            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
                  style={{ background: `${selected.color}1a`, color: selected.color }}>
              {selected.name}
            </span>
          ) : (
            <span className="flex items-center gap-1 overflow-hidden">
              <span className="h-2 w-2 rounded-full shrink-0" style={{ background: selected.color }} />
              <span className="truncate">{selected.name}</span>
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <input value={search}
               onChange={(e) => setSearch(e.target.value)}
               placeholder={`Filter ${placeholder.toLowerCase()}…`}
               className="w-full text-sm px-2 py-1.5 rounded-md border border-slate-200 focus:border-[#145c8c] outline-none mb-2"
               data-testid={`list-${testIdSuffix}-search-${entryId}`} />
        <div className="max-h-48 overflow-y-auto">
          {value && (
            <button onClick={() => pick(value)}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-xs text-slate-500 border-b border-slate-100 mb-1"
                    data-testid={`list-${testIdSuffix}-clear-${entryId}`}>
              Clear selection
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="px-2 py-3 text-xs text-slate-400 text-center">No matches.</div>
          ) : filtered.map((i) => {
            const isOn = value === i.id;
            return (
              <button key={i.id} onClick={() => pick(i.id)}
                      className={`w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 text-sm ${isOn ? "bg-slate-50" : ""}`}
                      data-testid={`list-${testIdSuffix}-option-${entryId}-${i.id}`}>
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: i.color }} />
                <span className="flex-1 truncate">{i.name}</span>
                {isOn && <Check className="h-3.5 w-3.5 text-[#145c8c]" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------- editable row ----------
function EntryRow({ entry, clients, activities, tags, clientMap, activityMap, tagMap, onEdit, onDelete, onResume }) {
  const isLeave = !!entry.leave_type;
  // Only the 9 PM auto-stop warrants a highlight — overlap auto-stops are
  // silent (expected behaviour when a scheduled item takes over).
  const showAutoStopWarning = entry.auto_stopped_reason === "past_9pm";
  const autoStopMsg = showAutoStopWarning
    ? "Auto-stopped at 9 PM — looked like you forgot to stop the timer. Edit the finish time if needed."
    : "";
  const initialClientIds = Array.isArray(entry.client_ids) && entry.client_ids.length
    ? entry.client_ids
    : (entry.client_id ? [entry.client_id] : []);

  const [desc, setDesc] = useState(entry.description || "");
  const [clientIds, setClientIds] = useState(initialClientIds);
  const [activityId, setActivityId] = useState(entry.activity_id || null);
  const [tagIds, setTagIds] = useState(Array.isArray(entry.tag_ids) ? entry.tag_ids : []);

  const startD = new Date(entry.start);
  const endD = entry.end ? new Date(entry.end) : new Date(entry.start);
  const [dateStr, setDateStr] = useState(toDateStr(startD));
  const [startTime, setStartTime] = useState(toTimeStr(startD));
  const [endTime, setEndTime] = useState(toTimeStr(endD));

  // re-seed if server pushes a refreshed entry
  const lastSeedRef = useRef(entry.updated_at || entry.id);
  useEffect(() => {
    const seed = entry.updated_at || entry.id;
    if (seed === lastSeedRef.current) return;
    lastSeedRef.current = seed;
    setDesc(entry.description || "");
    setClientIds(Array.isArray(entry.client_ids) && entry.client_ids.length
      ? entry.client_ids
      : (entry.client_id ? [entry.client_id] : []));
    setActivityId(entry.activity_id || null);
    setTagIds(Array.isArray(entry.tag_ids) ? entry.tag_ids : []);
    const s = new Date(entry.start);
    const e = entry.end ? new Date(entry.end) : new Date(entry.start);
    setDateStr(toDateStr(s));
    setStartTime(toTimeStr(s));
    setEndTime(toTimeStr(e));
  }, [entry]);

  const save = (overrides = {}) => {
    const finalDate = overrides.dateStr ?? dateStr;
    const finalStartTime = overrides.startTime ?? startTime;
    const finalEndTime = overrides.endTime ?? endTime;
    let startDt = parseDateTime(finalDate, finalStartTime);
    let endDt = parseDateTime(finalDate, finalEndTime);
    // If end is before start (e.g. crossing midnight), push end to next day
    if (endDt < startDt) endDt = new Date(endDt.getTime() + 86400000);

    const payload = {
      description: overrides.description ?? desc,
      client_ids: overrides.clientIds ?? clientIds,
      activity_id: overrides.activityId !== undefined ? overrides.activityId : activityId,
      tag_ids: overrides.tagIds ?? tagIds,
      leave_type: entry.leave_type || null,
      start: startDt.toISOString(),
      end: endDt.toISOString(),
    };
    onEdit && onEdit(entry.id, payload);
  };

  // detect duration
  const dur = useMemo(() => {
    const s = parseDateTime(dateStr, startTime);
    let e = parseDateTime(dateStr, endTime);
    if (e < s) e = new Date(e.getTime() + 86400000);
    return Math.max(0, Math.floor((e - s) / 1000));
  }, [dateStr, startTime, endTime]);

  // colour priority: category → client → activity
  const primaryCat = tagIds.length ? tagMap[tagIds[0]] : null;
  const primaryClient = clientIds.length ? clientMap[clientIds[0]] : null;
  const primaryAct = activityId ? activityMap[activityId] : null;
  const color = isLeave ? "#64748b" : (primaryCat?.color || primaryClient?.color || primaryAct?.color || "#145c8c");

  return (
    <li className={`px-4 py-2 flex items-center gap-2 hover:bg-slate-50/60 ${
          showAutoStopWarning ? "bg-amber-50/60 border-l-2 border-amber-400" : ""
        }`}
        data-testid={`list-entry-${entry.id}`}
        data-leave={isLeave ? "true" : "false"}
        data-auto-stopped={entry.auto_stopped_reason || undefined}
        title={autoStopMsg || undefined}>
      <span className="h-3 w-3 rounded-full shrink-0" style={{ background: color }} />
      {showAutoStopWarning && (
        <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0"
                       data-testid={`list-entry-autostop-icon-${entry.id}`} />
      )}

      {/* Description (or leave label) */}
      <div className="min-w-0 flex-1">
        {isLeave ? (
          <span className="uppercase tracking-widest text-[10px] text-slate-500"
                data-testid={`list-entry-leave-${entry.id}`}>
            {entry.leave_type === "annual" ? "Annual Leave"
              : entry.leave_type === "personal" ? "Personal Leave"
              : "Sick Leave"}
          </span>
        ) : (
          <input
            type="text"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            onBlur={() => {
              if ((entry.description || "") !== desc) save();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") {
                setDesc(entry.description || "");
                e.currentTarget.blur();
              }
            }}
            placeholder="(no description)"
            className="w-full bg-transparent text-sm font-medium text-slate-800 outline-none border border-transparent focus:border-slate-200 rounded px-1 py-0.5 placeholder:text-slate-400 placeholder:italic"
            data-testid={`list-desc-input-${entry.id}`}
          />
        )}
      </div>

      {/* Client / Activity / Category pickers */}
      {!isLeave && (
        <div className="flex items-center gap-1 shrink-0">
          <ClientPickerInline
            items={clients}
            value={clientIds}
            onChange={(next) => {
              if (!entry.leave_type && (!next || next.length === 0)) {
                // Block clearing the last client on a work entry — client is required.
                return;
              }
              setClientIds(next);
              if (!arraysEqual(next, initialClientIds) || !arraysEqual(next, clientIds)) {
                save({ clientIds: next });
              }
            }}
            entryId={entry.id}
          />
          <SinglePickerInline
            icon={TagIcon}
            items={activities}
            value={activityId}
            onChange={(next) => {
              setActivityId(next);
              save({ activityId: next });
            }}
            placeholder="Activity"
            entryId={entry.id}
            testIdSuffix="activity"
          />
          <SinglePickerInline
            icon={Layers}
            items={tags}
            value={tagIds[0] || null}
            onChange={(next) => {
              const arr = next ? [next] : [];
              setTagIds(arr);
              save({ tagIds: arr });
            }}
            placeholder="Category"
            entryId={entry.id}
            testIdSuffix="category"
            pill
          />
        </div>
      )}

      {/* Start / End times (inline editable) */}
      <div className="flex items-center gap-1 text-xs text-slate-500 shrink-0">
        <TimePicker
          value={startTime}
          onChange={(v) => {
            setStartTime(v);
            if (toTimeStr(new Date(entry.start)) !== v) save({ startTime: v });
          }}
          size="sm"
          className="w-20"
          testId={`list-start-input-${entry.id}`}
        />
        <span className="text-slate-300">–</span>
        <TimePicker
          value={endTime}
          onChange={(v) => {
            setEndTime(v);
            if (entry.end && toTimeStr(new Date(entry.end)) !== v) save({ endTime: v });
          }}
          size="sm"
          className="w-20"
          testId={`list-end-input-${entry.id}`}
        />
      </div>

      {/* Date (inline editable) */}
      <DatePicker
        value={dateStr}
        onChange={(v) => {
          setDateStr(v);
          if (toDateStr(new Date(entry.start)) !== v) save({ dateStr: v });
        }}
        size="sm"
        className="w-40"
        testId={`list-date-input-${entry.id}`}
      />

      {/* Duration */}
      <div className="text-sm font-semibold text-slate-700 timer-font shrink-0 w-20 text-right"
           data-testid={`list-duration-${entry.id}`}>
        {fmtDuration(dur)}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        {!isLeave && onResume && (
          <button
            onClick={() => onResume(entry)}
            className="p-1.5 rounded-md text-[#145c8c] hover:bg-[#e8f1f8] hover:text-[#0b2c47] transition-colors"
            title="Resume this entry"
            data-testid={`list-resume-${entry.id}`}
          >
            <Play className="h-3.5 w-3.5 fill-current" />
          </button>
        )}
        <button
          onClick={() => {
            if (window.confirm(`Delete this ${entry.description || "entry"}?`)) {
              onDelete && onDelete(entry.id);
            }
          }}
          className="p-1.5 hover:bg-red-50 rounded-md text-red-500"
          data-testid={`list-delete-${entry.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  );
}

// ---------- main list view ----------
export default function TimerListView({
  entries, clients, activities, tags,
  onCreate, onEdit, onDelete, onResume,
  onCreateClient, onCreateActivity,
}) {
  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const activityMap = useMemo(() => Object.fromEntries(activities.map((a) => [a.id, a])), [activities]);
  const tagMap = useMemo(() => Object.fromEntries((tags || []).map((t) => [t.id, t])), [tags]);

  // Draft for the "Add entry" modal (uses the shared WeekCalendar EntryEditor)
  const [draft, setDraft] = useState(null);

  const openNew = () => {
    const now = new Date();
    const start = new Date(now);
    start.setMinutes(start.getMinutes() - 60, 0, 0);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    setDraft({
      description: "",
      client_ids: [],
      activity_id: null,
      tag_ids: [],
      start: start.toISOString(),
      end: end.toISOString(),
    });
  };

  const handleSave = async (payload) => {
    if (!onCreate) return;
    await onCreate(payload);
    setDraft(null);
  };

  const toolbar = (
    <div className="flex items-center justify-between px-6 py-3 border-b border-slate-100 bg-white sticky top-0 z-10">
      <div className="text-sm text-slate-500">
        Manual entries for past time — pick a date, time range, client & activity.
      </div>
      {onCreate && (
        <button
          onClick={openNew}
          className="h-9 px-3 rounded-md text-white text-sm flex items-center gap-1.5"
          style={{ background: "#145c8c" }}
          data-testid="list-add-entry"
        >
          <Plus className="h-4 w-4" /> Add entry
        </button>
      )}
    </div>
  );

  const editor = (
    <EntryEditor
      open={!!draft}
      onClose={() => setDraft(null)}
      entry={null}
      draft={draft}
      clients={clients}
      activities={activities}
      tags={tags}
      onSave={handleSave}
      onDelete={() => setDraft(null)}
      onCreateClient={onCreateClient}
      onCreateActivity={onCreateActivity}
    />
  );

  const groups = useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      if (!e.end) continue;
      const d = new Date(e.start);
      const key = d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate());
      if (!m.has(key)) m.set(key, { date: new Date(d.getFullYear(), d.getMonth(), d.getDate()), items: [] });
      m.get(key).items.push(e);
    }
    const arr = Array.from(m.values());
    arr.sort((a, b) => b.date - a.date);
    for (const g of arr) {
      g.items.sort((a, b) => new Date(b.start) - new Date(a.start));
      g.totalSeconds = g.items.reduce((acc, e) => acc + (e.duration_seconds || 0), 0);
    }
    return arr;
  }, [entries]);

  if (groups.length === 0) {
    return (
      <div className="flex-1 flex flex-col" data-testid="timer-list-empty">
        {toolbar}
        <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
          No time entries yet in this range. Click <span className="mx-1 font-medium text-slate-600">Add entry</span> to record one.
        </div>
        {editor}
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-slate-50/40" data-testid="timer-list-view">
      {toolbar}
      <div className="max-w-6xl mx-auto px-6 py-4 space-y-4">
        {groups.map((g) => (
          <div key={g.date.toISOString()} className="bg-white border border-slate-200 rounded-xl overflow-hidden"
               data-testid={`list-group-${g.date.toISOString().slice(0, 10)}`}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
              <div className="text-sm font-semibold text-slate-700">
                {dayHeading(g.date)}
                <span className="ml-2 text-xs font-normal text-slate-400">
                  {g.date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                </span>
              </div>
              <div className="text-xs text-slate-500">
                Day total <span className="ml-1 font-semibold text-slate-700 timer-font"
                                data-testid={`list-day-total-${g.date.toISOString().slice(0, 10)}`}>
                  {fmtHM(g.totalSeconds)}
                </span>
              </div>
            </div>

            <ul className="divide-y divide-slate-100">
              {g.items.map((e) => (
                <EntryRow
                  key={e.id}
                  entry={e}
                  clients={clients}
                  activities={activities}
                  tags={tags || []}
                  clientMap={clientMap}
                  activityMap={activityMap}
                  tagMap={tagMap}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onResume={onResume}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>
      {editor}
    </div>
  );
}
